// src/features/websites/components/EndUserProfileSection.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  Button,
  useTheme,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { ListenerProfile } from '../../../../../types/studio.types';
import { availableContentTypes, availableInterests, prebuiltEndUserProfiles } from '../../../../../data/prebuiltProfiles';
import LocationSelector from '../../../../locations/LocationSelector';
import CollapsibleSection from '../../../../../components/common/CollapsibleSection';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import InterestsIcon from '@mui/icons-material/Interests';
import MovieIcon from '@mui/icons-material/Movie';
import SettingsIcon from '@mui/icons-material/Settings';
import ListenerConfigPanel, { ListenerSettings } from './ListenerConfigPanel';

// Helper function to compare objects for deep equality
const isEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => isEqual(obj1[key], obj2[key]));
};

interface ListenerProfileSectionProps {
  endUserProfile: ListenerProfile;
  selectedEndUserProfileId: string;
  originalProfileName?: string; // Name of the original profile that was customized
  customProfileId?: string; // ID used for custom profiles
  isLoading: boolean;
  onProfileChange: (field: keyof ListenerProfile, value: any) => void;
  onSelectPrebuiltProfile: (event: SelectChangeEvent<string>) => void;
  listenerSettings?: ListenerSettings;
  onListenerSettingsChange?: (settings: ListenerSettings) => void;
  icon?: React.ReactNode;
  /**
   * Namespace to differentiate this component's usage context
   * e.g., 'studio-tab' or 'preview-page'
   */
  namespace?: string;
}

const ListenerProfileSection: React.FC<ListenerProfileSectionProps> = ({
  endUserProfile,
  selectedEndUserProfileId,
  originalProfileName,
  customProfileId,
  isLoading,
  onProfileChange,
  onSelectPrebuiltProfile,
  icon,
  namespace = 'default',
  listenerSettings: initialListenerSettings,
  onListenerSettingsChange
}) => {
  const theme = useTheme();
  
  // Local storage key for listener settings
  const LISTENER_SETTINGS_KEY = `listener_settings_${namespace}`;
  
  // Keep track of if changes are from external props or local updates
  const isInternalUpdate = useRef(false);
  // Track if we've loaded from localStorage already
  const hasLoadedFromLocalStorage = useRef(false);
  
  // Initialize listener settings from props or create default settings
  const [listenerSettings, setListenerSettings] = useState<ListenerSettings>(
    initialListenerSettings || { mood: 'Neutral', soundscape: 'Silence', informality: 5 }
  );

  // Load listener settings from localStorage on mount only
  useEffect(() => {
    if (hasLoadedFromLocalStorage.current) {
      return; // Prevent reloading if we've already loaded once
    }

    try {
      const savedSettings = localStorage.getItem(LISTENER_SETTINGS_KEY);
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings) as ListenerSettings;
        isInternalUpdate.current = true;
        setListenerSettings(parsedSettings);
        hasLoadedFromLocalStorage.current = true;
      }
    } catch (error) {
      console.error('Error loading listener settings from localStorage:', error);
    }
  }, [LISTENER_SETTINGS_KEY]);

  // Handle prop changes for listenerSettings - if external props change, update the state
  // But only if they're different from current state to avoid loops and have not been loaded from localStorage
  useEffect(() => {
    // Only update from props if:
    // 1. We have initialListenerSettings (not undefined)
    // 2. It's not from our internal update
    // 3. The props are different from state
    // 4. We haven't already loaded from localStorage (localStorage has priority)
    if (
      initialListenerSettings && 
      !isInternalUpdate.current && 
      !isEqual(initialListenerSettings, listenerSettings) &&
      !hasLoadedFromLocalStorage.current
    ) {
      setListenerSettings(initialListenerSettings);
    }
    isInternalUpdate.current = false;
  }, [initialListenerSettings, listenerSettings]);

  // Save listener settings to localStorage when they change and notify parent
  useEffect(() => {
    try {
      localStorage.setItem(LISTENER_SETTINGS_KEY, JSON.stringify(listenerSettings));
      
      // Call the parent component's onChange handler if provided, but only for internal updates
      if (onListenerSettingsChange && isInternalUpdate.current) {
        onListenerSettingsChange(listenerSettings);
      }
    } catch (error) {
      console.error('Error saving listener settings to localStorage:', error);
    }
  }, [listenerSettings, LISTENER_SETTINGS_KEY, onListenerSettingsChange]);

  // Handle listener settings changes
  const handleListenerSettingsChange = (settings: ListenerSettings) => {
    // Mark this as an internal update before setting state
    isInternalUpdate.current = true;
    setListenerSettings(settings);
    
    // Notify parent of both listener settings changes AND profile changes to trigger custom profile creation
    onListenerSettingsChange?.(settings);
    
    // Trigger creation of custom profile just like other form field changes do
    if (onProfileChange && selectedEndUserProfileId !== customProfileId) {
      // We'll use the listenerSettings field to store these settings in the profile
      onProfileChange('listenerSettings', settings);
      // Also update the conversationalInformality field to match the informality setting
      onProfileChange('conversationalInformality', settings.informality);
    }
  };

  // Function to handle reset to default
  const handleReset = () => {
    const defaultSettings: ListenerSettings = { mood: 'Neutral', soundscape: 'Silence', informality: 5 };
    isInternalUpdate.current = true;
    setListenerSettings(defaultSettings);
    
    // Also save to localStorage
    try {
      localStorage.setItem(LISTENER_SETTINGS_KEY, JSON.stringify(defaultSettings));
      
      if (onListenerSettingsChange) {
        onListenerSettingsChange(defaultSettings);
      }
    } catch (error) {
      console.error('Error saving default listener settings to localStorage:', error);
    }
  };

  return (
    <CollapsibleSection 
      title={`Listener Profile for Testing ${endUserProfile.name}` }
      icon={icon}
      id="listener-profile-section"
      namespace={namespace}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography 
          variant="body1" 
          color="text.secondary" 
          paragraph
          sx={{ mb: 3 }}
        >
          Configure the ad-hoc listener profile to personalize the generated test content. 
          This profile will only be used when testing the configuration.
        </Typography>

        {/* Select pre-built end-user profile */}
        <Box sx={{ mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="end-user-profile-select-label">Select Pre-built Profile</InputLabel>
            <Select
              labelId="end-user-profile-select-label"
              id="end-user-profile-select"
              value={selectedEndUserProfileId}
              label="Select Pre-built Profile"
              onChange={onSelectPrebuiltProfile}
              disabled={isLoading}
              sx={{ mb: 1 }}
            >
              <MenuItem value="">
                <em>Select a profile</em>
              </MenuItem>
              {/* Custom profile option - shown only when a profile has been customized */}
              {customProfileId && selectedEndUserProfileId === customProfileId && (
                <MenuItem key={customProfileId} value={customProfileId}>
                  {endUserProfile.name ? `${endUserProfile.name}` : originalProfileName} - Custom Profile
                </MenuItem>
              )}
              {/* Regular pre-built profiles */}
              {prebuiltEndUserProfiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name} - {profile.description}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {selectedEndUserProfileId === customProfileId 
                ? "You are using a customized profile. Your changes are saved automatically."
                : "Choose a pre-built profile as a starting point, then customize it below"}
            </FormHelperText>
          </FormControl>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Basic Info Section */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" component="h3">
                  End-User Name
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                value={endUserProfile?.name || ''}
                onChange={(e) => onProfileChange('name', e.target.value)}
                disabled={isLoading}
                placeholder="e.g., David"
                helperText="The name that will be used in the personalized greeting"
                variant="outlined"
              />
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" component="h3">
                  Preferred Pronouns
                </Typography>
              </Box>
              
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={endUserProfile?.preferredPronouns || 'they/them'}
                  onChange={(e) => onProfileChange('preferredPronouns', e.target.value)}
                  name="pronouns-group"
                  defaultValue="they/them"
                >
                  <FormControlLabel 
                    value="he/him" 
                    control={<Radio color="primary" />} 
                    label="He/Him" 
                    disabled={isLoading} 
                  />
                  <FormControlLabel 
                    value="she/her" 
                    control={<Radio color="primary" />} 
                    label="She/Her" 
                    disabled={isLoading} 
                  />
                  <FormControlLabel 
                    value="they/them" 
                    control={<Radio color="primary" />} 
                    label="They/Them" 
                    disabled={isLoading} 
                  />
                </RadioGroup>
                <FormHelperText>
                  Select preferred pronouns for personalized content
                </FormHelperText>
              </FormControl>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Location Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocationOnIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" component="h3">
              Location
            </Typography>
          </Box>

          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              backgroundColor: 'action.hover', 
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <LocationSelector
              value={endUserProfile?.location || ''}
              onChange={(value) => onProfileChange('location', value)}
              disabled={isLoading}
            />
          </Paper>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Background Description */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <DescriptionIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" component="h3">
              Background Description
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            value={endUserProfile?.backgroundDescription || ''}
            onChange={(e) => onProfileChange('backgroundDescription', e.target.value)}
            disabled={isLoading}
            placeholder="Describe the user's background, history, personal details, and any other relevant information"
            helperText="Add a rich description of the user's background to create more personalized content"
            variant="outlined"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
              }
            }}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Interests and Content Types */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InterestsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" component="h3">
                  Interests
                </Typography>
              </Box>
              
              <Autocomplete
                multiple
                options={[...availableInterests].sort((a, b) => a.localeCompare(b))}
                disableCloseOnSelect
                getOptionLabel={(option) => option}
                value={endUserProfile?.interests || []}
                onChange={(event, value) => onProfileChange('interests', value)}
                isOptionEqualToValue={(option, value) => option === value}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Interests"
                    placeholder="Select interests"
                  />
                )}
              />
              <FormHelperText>
                Select the user's interests
              </FormHelperText>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MovieIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" component="h3">
                  Preferred Content Types
                </Typography>
              </Box>
              
              <Autocomplete
                multiple
                options={[...availableContentTypes].sort((a, b) => a.localeCompare(b))}
                disableCloseOnSelect
                getOptionLabel={(option) => option}
                value={endUserProfile?.preferredContentTypes || []}
                onChange={(event, value) => onProfileChange('preferredContentTypes', value)}
                isOptionEqualToValue={(option, value) => option === value}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Content Types"
                    placeholder="Select content types"
                  />
                )}
              />
              <FormHelperText>
                Select the types of content the user prefers
              </FormHelperText>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Listener Config Panel */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SettingsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" component="h3">
              Listener Preferences
            </Typography>
          </Box>

          <ListenerConfigPanel
            settings={listenerSettings}
            onChange={handleListenerSettingsChange}
            disabled={isLoading}
          />
        </Box>

        {/* Reset Button */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            mt: 4,
            pt: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset to Default
          </Button>
        </Box>
      </Paper>
    </CollapsibleSection>
  );
};

export default ListenerProfileSection;